import React, { useState, useEffect } from "react";
import {
  Checkbox,
  ComboBox,
  Form,
  FormGroup,
  Modal,
  Select,
  SelectItem,
  SelectItemGroup,
  TextInput,
} from "carbon-components-react";
import {
  ReferenceTaskShape,
  TaskConfigParamOptions,
  TaskOwner,
  TASK_CATEGORIES_OPTIONS_BY_OWNER,
  TASK_OWNER_OPTIONS,
  TASK_STAGE_OPTIONS_BY_OWNER,
} from "@eave/shared/types/api/tasks";

import getHandlers from "web_modules/shared/FormHelpers/eventHandlers";
import Validations from "web_modules/shared/FormHelpers/validation";
import TaskClauseEditor from "underwriter_dashboard/hurdle_editors/Tasks/TaskClauseEditors/TaskClauseEditor";
import EditDueDateParameters from "underwriter_dashboard/hurdle_editors/Tasks/TaskClauseEditors/DueDateParameters";
import { EditingState } from "web_modules/shared/BaseTable";
import {
  newTaskDefaultIndexOrder,
  taskOrderInitialSelectedItem,
  taskOrderItems,
} from "underwriter_dashboard/hurdle_editors/Tasks/helpers";
import { ReferenceTaskFormShape } from "./TaskConfig";

const styles = require("./styles.module.scss");

interface TaskConfigModalFormProps {
  isOpen: boolean;
  editingMode: EditingState;
  formState: ReferenceTaskFormShape;
  setFormState: Function;
  taskOptions: TaskConfigParamOptions;
  tasks: ReferenceTaskShape[];

  // Modal props
  onRequestSubmit: Function;
  onRequestClose: Function;
}

const TaskConfigModalForm: React.FC<TaskConfigModalFormProps> = ({
  isOpen,
  editingMode,
  formState,
  setFormState,
  taskOptions,
  tasks,

  onRequestSubmit,
  onRequestClose,
}: TaskConfigModalFormProps) => {
  const [invalidFields, setInvalidFields] = useState([]);
  const [disableModalSubmit, setDisableModalSubmit] = useState(false);

  const [defaultIndexOrder, setDefaultIndexOrder] = useState(0);

  useEffect(() => {
    if (!formState || !formState.stage) {
      return;
    }

    setDefaultIndexOrder(newTaskDefaultIndexOrder(tasks, formState.stage));
  }, [tasks, formState]);

  const { handleTextInputChange, handleSelectChange, handleCheckboxChange, handleComboBoxChange } =
    getHandlers(formState, setFormState, invalidFields, setInvalidFields);

  return (
    <Modal
      open={isOpen}
      modalHeading={editingMode === EditingState.CREATE ? "Add task" : "Edit task"}
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      primaryButtonDisabled={disableModalSubmit}
      onRequestSubmit={onRequestSubmit}
      onRequestClose={onRequestClose}
      onBlur={onRequestClose}
    >
      <Form className={styles.taskLogicForm}>
        <FormGroup legendText="Details">
          <TextInput
            id="task-name"
            name="name"
            value={(formState && formState.name) || ""}
            labelText="Name *"
            placeholder="Enter a new task name"
            type="text"
            invalid={invalidFields.includes("name")}
            invalidText="Enter a valid name"
            onBlur={e => handleTextInputChange(e, "name", [Validations.IsValid])}
            onChange={e => handleTextInputChange(e, "name", [Validations.IsValid])}
            size="sm"
          />
          <div style={{ marginBottom: "10px" }} />
          <Select
            id="task-owner"
            name="owner"
            value={(formState && formState.owner) || ""}
            invalid={invalidFields.includes("owner")}
            invalidText="A valid value is required"
            labelText="Task Owner *"
            style={{ minWidth: "320px" }}
            onBlur={e => {
              setFormState({ ...formState, owner: e.target.value, stage: "", category: "" });
            }}
            onChange={e => {
              setFormState({ ...formState, owner: e.target.value, stage: "", category: "" });
            }}
            size="sm"
          >
            <SelectItem text="Select an owner" value="" />
            {TASK_OWNER_OPTIONS.map(owner => (
              <SelectItem key={owner} text={owner} value={owner} />
            ))}
          </Select>
          <div style={{ marginBottom: "10px" }} />
          <Select
            id="task-stage"
            disabled={formState && !formState.owner}
            name="stage"
            value={(formState && formState.stage) || ""}
            invalid={invalidFields.includes("stage")}
            invalidText={
              formState && formState.owner ? "Select a stage" : "Select a task owner first"
            }
            labelText="Task Stage *"
            onBlur={e => handleSelectChange(e, "stage")}
            onChange={e => handleSelectChange(e, "stage")}
            size="sm"
          >
            <SelectItem text="Select a stage" value="" />
            {formState &&
              formState.owner === TaskOwner.Underwriter &&
              TASK_STAGE_OPTIONS_BY_OWNER[TaskOwner.Underwriter].map(stage => (
                <SelectItem key={stage} text={stage} value={stage} />
              ))}
            {formState &&
              formState.owner === TaskOwner.LMLender &&
              TASK_STAGE_OPTIONS_BY_OWNER[TaskOwner.LMLender].map(stage => (
                <SelectItem key={stage} text={stage} value={stage} />
              ))}
          </Select>
          <div style={{ marginBottom: "10px" }} />
          <Select
            id="task-category"
            name="category"
            disabled={formState && !formState.owner && !formState.stage}
            value={(formState && formState.category) || ""}
            invalid={invalidFields.includes("category")}
            invalidText="A valid value is required"
            labelText="Task Category *"
            style={{ minWidth: "320px" }}
            onBlur={e => handleSelectChange(e, "category", [Validations.IsValid])}
            onChange={e => handleSelectChange(e, "category", [Validations.IsValid])}
            size="sm"
          >
            <SelectItem text="Select a category" value="" />
            {formState && formState.owner === TaskOwner.Underwriter && (
              <SelectItemGroup label={TaskOwner.Underwriter}>
                {TASK_CATEGORIES_OPTIONS_BY_OWNER[TaskOwner.Underwriter].map(cat => (
                  <SelectItem key={cat} text={cat} value={cat} />
                ))}
              </SelectItemGroup>
            )}
            {formState && formState.owner === TaskOwner.LMLender && (
              <SelectItemGroup label={TaskOwner.LMLender}>
                {TASK_CATEGORIES_OPTIONS_BY_OWNER[TaskOwner.LMLender].map(cat => (
                  <SelectItem key={cat} text={cat} value={cat} />
                ))}
              </SelectItemGroup>
            )}
          </Select>
          <div style={{ marginBottom: "10px" }} />
          <Checkbox
            id="task-include-in-queues"
            name="include_in_queues"
            labelText="Include in queues?"
            checked={(formState && formState.include_in_queues) || false}
            onChange={() => handleCheckboxChange(formState.include_in_queues, "include_in_queues")}
          />
          <Checkbox
            id="task-is-critical"
            name="is_critical"
            labelText="Is critical?"
            checked={(formState && formState.is_critical) || false}
            onChange={() => handleCheckboxChange(formState.is_critical, "is_critical")}
          />
          <Checkbox
            id="task-pinned"
            name="pinned"
            labelText="Pinned?"
            checked={(formState && formState.pinned) || false}
            onChange={() => handleCheckboxChange(formState.pinned, "pinned")}
          />
          <Checkbox
            id="task-blocks-loan-decision"
            name="blocks_loan_decision"
            labelText="Block Loan Decision?"
            checked={(formState && formState.blocks_loan_decision) || false}
            onChange={() =>
              handleCheckboxChange(formState.blocks_loan_decision, "blocks_loan_decision")
            }
          />
          <div style={{ marginBottom: "10px" }} />
          <ComboBox
            id="reference-task-order"
            titleText="Which task should appear after this one?"
            name="index_order"
            disabled={!formState?.stage}
            placeholder="(Add to start)" // defaults to top of the list
            light
            items={taskOrderItems(tasks, formState, defaultIndexOrder)}
            itemToString={e => (e ? e.text : "")}
            selectedItem={
              editingMode === EditingState.CREATE
                ? taskOrderInitialSelectedItem(tasks, {
                    ...formState,
                    index_order: formState.index_order,
                  })
                : taskOrderItems(tasks, formState, defaultIndexOrder).find(
                    t => t.value === formState.index_order,
                  )
            }
            invalid={invalidFields.includes("index_order")}
            invalidText="A valid value is required"
            onChange={e => handleComboBoxChange(e, "index_order", defaultIndexOrder)}
          />
        </FormGroup>
        <hr style={{ border: "2px dashed #e8e8e8" }} />
        <FormGroup legendText="Visibility Conditions">
          <TaskClauseEditor
            typeOfClause="visible"
            taskOptions={taskOptions}
            formState={formState && formState.visibility_conditions}
            setFormState={visibility_conditions => {
              // If user resets visibility_conditions, delete the key
              if (
                visibility_conditions.operator === "" &&
                formState.visibility_conditions !== undefined
              ) {
                setFormState({
                  ...formState,
                  visibility_conditions: null,
                });
                return;
              }
              setFormState({ ...formState, visibility_conditions });
            }}
            setDisableModalSubmit={setDisableModalSubmit}
          />
        </FormGroup>
        <hr style={{ border: "2px dashed #e8e8e8" }} />
        <FormGroup legendText="Due Date Parameters">
          <EditDueDateParameters
            options={taskOptions}
            formState={formState && formState.due_date_params}
            setFormState={due_date_params => {
              setFormState({ ...formState, due_date_params });
            }}
          />
        </FormGroup>
        <hr style={{ border: "2px dashed #e8e8e8" }} />
        <FormGroup legendText="Completion Conditions">
          <TaskClauseEditor
            typeOfClause="complete"
            taskOptions={taskOptions}
            formState={formState && formState.completion_conditions}
            setFormState={completion_conditions => {
              // If user resets completion_conditions, delete the key
              if (
                completion_conditions.operator === "" &&
                formState.completion_conditions !== undefined
              ) {
                setFormState({
                  ...formState,
                  completion_conditions: null,
                });
                return;
              }
              setFormState({ ...formState, completion_conditions });
            }}
            setDisableModalSubmit={setDisableModalSubmit}
          />
        </FormGroup>
      </Form>
    </Modal>
  );
};

export default TaskConfigModalForm;
